/**
 * Shopping cart state management using localStorage
 */
import { useState, useCallback } from 'react';
import { trackActivity } from '../utils/track';

const CART_KEY = 'truetide_cart';

const loadCart = () => {
  try {
    const c = localStorage.getItem(CART_KEY);
    return c ? JSON.parse(c) : [];
  } catch { return []; }
};

const saveCart = (cart) => localStorage.setItem(CART_KEY, JSON.stringify(cart));

export const useCart = () => {
  const [items, setItems] = useState(loadCart);

  const addItem = useCallback((variant, product, quantity = 1) => {
    setItems(prev => {
      const existing = prev.find(i => i.variantId === variant.id);
      const next = existing
        ? prev.map(i => i.variantId === variant.id
            ? { ...i, quantity: i.quantity + quantity } : i)
        : [...prev, {
            variantId: variant.id,
            productName: product.name,
            productSlug: product.slug,                     // photo is derived from slug at render time (always current)
            variantName: variant.name,
            priceInCents: variant.priceInCents,            // VIP (member) price
            comparePriceInCents: variant.comparePriceInCents, // retail (non-member) price
            imageUrl: product.imageUrl,                    // legacy fallback for carts saved before productSlug existed
            quantity,
          }];
      saveCart(next);
      return next;
    });
    // Behavioral signal for the admin timeline + marketing flow (non-blocking).
    trackActivity({
      type: 'cart_add',
      productId: product.id,
      productSlug: product.slug,
      productName: product.name,
      variantId: variant.id,
      variantName: variant.name,
      priceInCents: variant.priceInCents,
      quantity,
    });
  }, []);

  const removeItem = useCallback((variantId) => {
    setItems(prev => {
      const next = prev.filter(i => i.variantId !== variantId);
      saveCart(next);
      return next;
    });
  }, []);

  const updateQuantity = useCallback((variantId, quantity) => {
    if (quantity < 1) return;
    setItems(prev => {
      const next = prev.map(i => i.variantId === variantId ? { ...i, quantity } : i);
      saveCart(next);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  // Retail unit price for an item = comparePrice when it carries a markup, else
  // the VIP price (some variants have no retail markup).
  const retailUnit = (i) =>
    (i.comparePriceInCents && i.comparePriceInCents > i.priceInCents)
      ? i.comparePriceInCents
      : i.priceInCents;

  // `total` = VIP subtotal (kept for back-compat). `retailTotal` = what a
  // non-member pays. Components pick based on the shopper's membership.
  const total       = items.reduce((s, i) => s + i.priceInCents * i.quantity, 0);
  const retailTotal = items.reduce((s, i) => s + retailUnit(i) * i.quantity, 0);
  const count       = items.reduce((s, i) => s + i.quantity, 0);

  return { items, addItem, removeItem, updateQuantity, clearCart, total, retailTotal, retailUnit, count };
};
